import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipAnchorProps {
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  onPointerEnter: (event: PointerEvent<HTMLElement>) => void;
  onPointerLeave: (event: PointerEvent<HTMLElement>) => void;
  onFocus: (event: FocusEvent<HTMLElement>) => void;
  onBlur: (event: FocusEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export interface TooltipProps {
  /** One line, no interactive content (03-components §5.2). */
  label: string;
  /**
   * `labels`: the tooltip IS the control's accessible name (the icon rail, 02-patterns §1.4).
   * Otherwise it is supplementary and wired through aria-describedby.
   */
  labels?: boolean;
  placement?: TooltipPlacement;
  /** Render prop: spread the props onto the anchor element. */
  children: (props: TooltipAnchorProps) => ReactNode;
  className?: string;
  /** Open delay in ms (~300 per §5.2). */
  delay?: number;
}

/**
 * Opens on hover and on focus after a delay; closes on pointer-out, blur and Escape. Never opens
 * for a touch pointer — on touch the information must exist elsewhere, and the one sanctioned case
 * (the rail) does not render below --bp-md.
 */
export function Tooltip({ label, labels = false, placement = 'top', children, className, delay = 300 }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const scheduleOpen = useCallback(() => {
    clear();
    timer.current = window.setTimeout(() => setOpen(true), delay);
  }, [clear, delay]);

  const close = useCallback(() => {
    clear();
    setOpen(false);
  }, [clear]);

  useEffect(() => clear, [clear]);

  const anchorProps: TooltipAnchorProps = {
    ...(labels ? { 'aria-labelledby': id } : { 'aria-describedby': id }),
    onPointerEnter: (event) => {
      if (event.pointerType === 'touch') return;
      scheduleOpen();
    },
    onPointerLeave: close,
    onFocus: scheduleOpen,
    onBlur: close,
    onKeyDown: (event) => {
      if (event.key === 'Escape' && open) {
        event.stopPropagation();
        close();
      }
    },
  };

  return (
    <span className={['tooltip-anchor', className].filter(Boolean).join(' ')}>
      {children(anchorProps)}
      <span id={id} role="tooltip" className={`tooltip tooltip--${placement}`} data-open={open || undefined}>
        {label}
      </span>
    </span>
  );
}
